import { ObjectId } from "mongodb";

export interface EventType {
  _id?: string | ObjectId;
  id?: string;
  url: string;
  logo: { url: string };
  name: { text: string };
  description: { text: string };
  locationInfo: { localized_address_display: string };
  start: { local: string };
  end: { local: string };
  startDateTime: Date;
  EndDateTime: Date;
  attendeeInfo?: object[];
  source?: string;
  manual_fields?: string[];
  status?: string;
  thrive_deleted?: boolean;
  flyer?: {
    name: string;
    storageName: string;
    path: string;
    download: string;
  } | null;
}

export interface CaseType {
  _id: string | ObjectId;
  user: string;
  date: Date;
  firm: string;
  status: string;
  createdBy: string;
  user_2nd?: string;
}

export const defaultCase: CaseType = {
  _id: "",
  user: "",
  date: new Date(),
  firm: "",
  status: "",
  createdBy: "",
};

export interface UserType {
  _id: string | ObjectId;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string;
  token?: string;
  // Excluded from consultant statistics and reports (service accounts, admins).
  hideFromStats?: boolean;
}

export const defaultUser: UserType = {
  _id: "",
  username: "",
  password: "",
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  phone: "",
  token: "",
};

export type ReferralPipelineStatus =
  | "referred"
  | "appointment_scheduled"
  | "in_progress"
  | "completed"
  | "unsuccessful"
  | "follow_up_needed";

export interface ReferralStatusHistoryEntry {
  status: ReferralPipelineStatus;
  changed_at: Date;
  changed_by: string;
  note?: string;
}

export interface ReferralOutcome {
  assistance_provided?: boolean | null;
  objective_achieved?: boolean | null;
  outcome_note?: string;
  recorded_at?: Date;
  recorded_by?: string;
}

export interface ReferralType {
  _id: string | ObjectId;
  case_id: string | ObjectId;
  created_at: Date;
  firm_id: string | ObjectId;
  referral_note: string;
  referral_objective: string;
  resource_id: string | ObjectId;
  updated_at: Date;
  provider_id?: string | ObjectId;
  referral_type?: string;
  status?: string;
  pipeline_status?: ReferralPipelineStatus;
  status_history?: ReferralStatusHistoryEntry[];
  outcome?: ReferralOutcome;
  referred_by?: string;
}

export const defaultReferral: ReferralType = {
  _id: "",
  case_id: "",
  created_at: new Date(),
  firm_id: "",
  referral_note: "",
  referral_objective: "",
  resource_id: "",
  updated_at: new Date(),
  pipeline_status: "referred",
  status_history: [],
};

// Named OutcomeRecordType to avoid colliding with ReferralOutcome above —
// this is the Phase 5 economic-impact record (its own `outcomes` collection).
export type OutcomeRecordKind =
  | "business_started"
  | "business_expanded"
  | "jobs_created"
  | "jobs_retained"
  | "revenue_growth"
  | "capital_accessed"
  | "loan"
  | "grant"
  | "certification";

export interface OutcomeRecordType {
  _id?: string;
  case_id: string;
  firm_id: string;
  type: OutcomeRecordKind;
  value: number | null;
  date: Date | string;
  note: string;
  recorded_by?: string;
  source?: "manual" | "case_close" | "backfill_grants";
  status?: "active" | "deleted";
  created_at?: Date;
  updated_at?: Date;
}

export const defaultOutcome: OutcomeRecordType = {
  _id: "",
  case_id: "",
  firm_id: "",
  type: "business_started",
  value: null,
  date: new Date(),
  note: "",
};

export interface ReminderSourceType {
  // "rule" reminders are generated by the nightly follow-up job
  // (lib/daily_script/generateFollowUps.js) and carry rule/ref_id.
  kind: "manual" | "action_plan" | "rule";
  action_plan_id?: string;
  rule?: string;
  ref_id?: string;
}

export interface ReminderType {
  _id: string | ObjectId;
  user_id: string | ObjectId;
  user_email: string;
  case_id: string | null;
  firm_name: string | null;
  title: string;
  note: string;
  dueAt: Date | string;
  status: "pending" | "completed" | "cancelled";
  source: ReminderSourceType;
  ics?: {
    uid: string;
    sequence: number;
    lastMethod: "REQUEST" | "CANCEL" | null;
    lastSentAt: Date | string | null;
    lastError: string | null;
  };
  created_at: Date | string;
  updated_at: Date | string;
  completed_at: Date | string | null;
}

export const defaultReminder: ReminderType = {
  _id: "",
  user_id: "",
  user_email: "",
  case_id: null,
  firm_name: null,
  title: "",
  note: "",
  dueAt: new Date(),
  status: "pending",
  source: { kind: "manual" },
  created_at: new Date(),
  updated_at: new Date(),
  completed_at: null,
};

// Action Items replaced the retired Business Action Plan: a coaching-style
// goal plus a task checklist (stored in `flattened_action_plan`). The old
// administrative fields (businessObjective / timeLine / action / resource /
// note / startDate) are gone and are no longer written by the server.
export interface ActionItemTaskType {
  task_id: string;
  title: string;
  due_date?: Date | string | null;
  completed: boolean;
  completed_at?: Date | string | null;
  completed_by?: string | null;
  created_at?: Date | string;
}

// Files linked onto an Action Item. The bytes live in the shared per-case
// upload store (public/case_doc/<case_id>/), so a row only carries the storage
// pointer plus display metadata.
export interface ActionItemAttachmentType {
  storageName: string;
  originalName: string;
  mimeType?: string | null;
  size?: number | null;
  uploaded_at?: Date | string;
  uploaded_by?: string;
}

export interface ActionItemType {
  _id?: string;
  goal: string;
  tasks: ActionItemTaskType[];
  case_id: string;
  status?: string;
  attachments?: ActionItemAttachmentType[];
}

export const defaultActionItem: ActionItemType = {
  goal: "",
  tasks: [],
  case_id: "",
};

export interface BroadcastType {
  _id: string;
  broadcastType: string;
  subject: string;
  fromBFC: string;
  fromEmail: string;
  recipientCount: number;
  status: "queued" | "sending" | "sent" | "error";
  createdAt: string;
  sentAt: string | null;
  eventId?: string | null;
  recipientFilter: {
    bfc?: string;
    city?: string[];
    zipcode?: string[];
    bussType?: string[];
    subBussType?: string[];
    classification?: string[];
    certification?: string[];
    businessChannels?: string[];
    emergOrEst?: string;
  };
}

// One row of GET /dashboard/inactive — an open case with no recent activity.
// `lastActivity`/`daysInactive` are null when the case has never had activity.
export interface InactiveRow {
  case_id: string;
  firm_id: string | null;
  firm_name: string;
  lastActivity: string | null;
  daysInactive: number | null;
  owner_name: string | null;
}

export interface InactiveClientsResponse {
  rows: InactiveRow[];
  total: number;
  page: number;
  pageSize: number;
  inactiveDays: number;
  // Open cases inactive beyond the dashboard lane's upper bound. Sent by the
  // server for the dashboard's own bookkeeping; this page ignores it.
  olderTotal?: number;
}

// GET /reports/sbc-stats — on-screen SBC comparison numbers (Program Statistics).
export interface SbcStatsMetrics {
  consultingSessions: number;
  companiesServed: number;
  contactHours: number;
  prepHours: number;
  travelHours: number;
  resourceReferrals: number;
  b2b15min: number;
  b2bProviderReferrals: number;
  b2bReferralsCompleted: number;
  referralTypes: { [type: string]: number };
  minorityOwned: number;
  womenOwned: number;
  veteranOwned: number;
  caseCreated: number;
  initialContact: number;
  businessAssessment: number;
  caseClosed: number;
}

export interface SbcStatsUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  metrics: SbcStatsMetrics;
}

export interface SbcStatsResponse {
  range: { startDate: string | null; endDate: string };
  users: SbcStatsUser[]; // sorted by last name
  total: SbcStatsMetrics;
}
